export type DocumentStatus = 'complete' | 'pending' | 'incomplete' | 'deactivated';

export interface DocumentEntry {
  id: number;
  name: string;
  uploadDate: string;
  status: DocumentStatus;
  uploadedBy: string;
}

export interface DocumentApiEntry {
  id: number;
  document: string;
  upload_date: string;
  status: string;
  comments: string | null;
  file_path: string | null;
  uploaded_by: string | null;
}

export interface DocumentDownloadResponse {
  download_url: string;
  expires_in: number;
  filename: string;
  file_size: number;
  document_type: string;
  upload_date: string;
}

export interface DocumentRepositoryPresignedTarget {
  presigned_post: {
    url: string;
    fields: Record<string, string>;
  };
  s3_key: string;
  expires_in: number;
  max_file_size: number;
}

export interface DocumentRepositoryPresignedResponse {
  file: DocumentRepositoryPresignedTarget;
}

export interface SelectedRecipient {
  email: string;
  legal_entity_id: number | null;
}

export interface DocumentRepositoryConfirmRequest {
  document_type: string;
  file_s3_key: string;
  new_document_name?: string;
}

export const CUSTOM_DOCUMENT_OPTION_KEY = 'document-repository-custom';

export type DocumentRepositoryConfirmResponse = DocumentApiEntry;

export interface IgaCoordinatorContact {
  contact_name: string | null;
  email: string | null;
  status: string | null;
  last_signed: string | null;
  last_notified: string | null;
}

export interface LegalEntityContact {
  contact_name: string | null;
  legal_entity_name: string | null;
  /**
   * Numeric identifier for the Legal Entity this contact is associated with.
   * Not yet returned by the backend's /contacts endpoint (see BCR-253
   * follow-up) — expected to be added there; treated as optional/nullable
   * until then so the frontend can start threading it through the
   * confirm-upload payload (`selected_recipients[].legal_entity_id`).
   */
  legal_entity_id?: number | null;
  country: string | null;
  email: string | null;
  /**
   * Signature tracking status for this Contact + Legal Entity combination.
   * Sent directly by the backend (see BCR-253) as one of "Pending" |
   * "Complete" | "Not in the scope" — no frontend computation/derivation is
   * needed or performed. Type remains `string | null` since the value
   * originates from an external backend this repo does not own.
   */
  status: string | null;
  last_signed: string | null;
  last_notified: string | null;
}

export interface RecipientContact {
  key: string;
  contact_name: string | null;
  email: string | null;
  legal_entity_name: string | null;
  legal_entity_id: number | null;
}

export interface ContactsApiResponse {
  document_type: string;
  contacts: {
    iga_coordinators: IgaCoordinatorContact[];
    legal_entity_contacts: LegalEntityContact[];
  };
}

export type RecipientGroup = 'iga_coordinators' | 'iga_coordinators_and_contacts';

export const RECIPIENT_GROUP_LABELS: Record<RecipientGroup, string> = {
  iga_coordinators: 'IGA Coordinators',
  iga_coordinators_and_contacts: 'IGA Coordinators and Document Contact List',
};
